import { useEffect, useRef, useState } from "react";
import { listAccounts, patchAccount, reconnectAccount, startUnipileHostedAuth } from "../api/accounts.ts";
import { useAppActions } from "../app/AppProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import { summarizeAccounts } from "./Accounts.helpers.ts";
import { AccountMenu } from "./account-switcher/AccountMenu.tsx";
import { AccountTrigger } from "./account-switcher/AccountTrigger.tsx";
import { EmptyAccountSwitcher } from "./account-switcher/EmptyAccountSwitcher.tsx";
import { getAccountTriggerMeta } from "./account-switcher/trigger.ts";

export { AccountChip } from "./account-switcher/AccountChip.tsx";

/**
 * Header dropdown orchestration for connected accounts.
 *
 * This component owns account loading, mutations, and filter state. The dropdown,
 * row, trigger, and chip rendering live in src/views/account-switcher/*.
 */
export function AccountSwitcher() {
  const { state, dispatch } = useAppActions();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  function startEdit(sessionId: string, currentLabel: string) {
    setEditingId(sessionId);
    setEditingValue(currentLabel);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingValue("");
  }

  async function commitEdit(sessionId: string) {
    const next = editingValue.trim().slice(0, 128);
    if (!next) {
      cancelEdit();
      return;
    }

    const current = state.accounts.find((account) => account.sessionId === sessionId);
    if (current && current.label === next) {
      cancelEdit();
      return;
    }

    setSaving(true);
    try {
      const updated = await patchAccount(sessionId, { label: next });
      if (current) {
        dispatch({
          type: "UPSERT_ACCOUNT",
          account: { ...current, label: updated.label },
        });
      }
      toast.showToast("Conta renomeada", "success");
      cancelEdit();
    } catch (err) {
      toast.showToast((err as Error)?.message || "Falha ao renomear", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleReconnect(sessionId: string, label: string) {
    setReconnectingId(sessionId);
    try {
      const data = await reconnectAccount(sessionId);
      if (data?.url) {
        toast.showToast(`Abrindo QR de "${label}" \u2014 escaneie no celular`, "info");
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        toast.showToast("URL de reconex\u00e3o n\u00e3o retornada", "error");
      }
    } catch (err) {
      toast.showToast((err as Error)?.message || "Falha ao gerar QR de reconex\u00e3o", "error");
    } finally {
      setReconnectingId(null);
    }
  }

  async function setAsDefault(sessionId: string) {
    const current = state.accounts.find((account) => account.sessionId === sessionId);
    if (!current) return;

    try {
      await patchAccount(sessionId, { isDefault: true });
      for (const account of state.accounts) {
        dispatch({
          type: "UPSERT_ACCOUNT",
          account: { ...account, isDefault: account.sessionId === sessionId },
        });
      }
      toast.showToast(`"${current.label || sessionId}" definida como padr\u00e3o`, "success");
    } catch (err) {
      toast.showToast((err as Error)?.message || "Falha ao definir padr\u00e3o", "error");
    }
  }

  useEffect(() => {
    if (!state.authToken) return;
    listAccounts()
      .then((accounts) => dispatch({ type: "SET_ACCOUNTS", accounts }))
      .catch((err) => console.warn("[wcrm] listAccounts failed", err));
  }, [state.authToken, dispatch]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const summaries = summarizeAccounts(state.accounts);
  const filterSet = new Set(state.accountFilter);
  const triggerMeta = getAccountTriggerMeta(summaries, filterSet);

  function toggleFilter(sessionId: string) {
    const next = new Set(state.accountFilter);
    if (next.has(sessionId)) next.delete(sessionId);
    else next.add(sessionId);
    dispatch({ type: "SET_ACCOUNT_FILTER", sessionIds: Array.from(next) });
  }

  function clearFilter() {
    dispatch({ type: "SET_ACCOUNT_FILTER", sessionIds: [] });
  }

  async function handleConnectUnipile() {
    setLinking(true);
    try {
      const data = await startUnipileHostedAuth();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.showToast("URL de pareamento n\u00e3o retornada", "error");
      }
    } catch (err) {
      toast.showToast((err as Error)?.message || "Falha ao gerar link de pareamento", "error");
    } finally {
      setLinking(false);
    }
  }

  return (
    <div className="account-switcher" ref={containerRef}>
      {!summaries.length ? (
        <EmptyAccountSwitcher linking={linking} onConnect={handleConnectUnipile} />
      ) : (
        <>
          <AccountTrigger
            meta={triggerMeta}
            open={open}
            onToggle={() => setOpen((value) => !value)}
          />
          {open ? (
            <AccountMenu
              summaries={summaries}
              filterSet={filterSet}
              linking={linking}
              editingId={editingId}
              editingValue={editingValue}
              saving={saving}
              reconnectingId={reconnectingId}
              onClearFilter={clearFilter}
              onConnect={handleConnectUnipile}
              onToggleFilter={toggleFilter}
              onStartEdit={startEdit}
              onCancelEdit={cancelEdit}
              onCommitEdit={(sessionId) => void commitEdit(sessionId)}
              onEditingValueChange={setEditingValue}
              onReconnect={(sessionId, label) => void handleReconnect(sessionId, label)}
              onSetDefault={(sessionId) => void setAsDefault(sessionId)}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
